import React, {
  FC, useState,
} from 'react';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Tooltip from '@mui/material/Tooltip';
import { useTranslation } from 'react-i18next';
import LeftAngle from '../../assets/icons/left-angle.svg';
import RightAngle from '../../assets/icons/right-angle.svg';
import LoadingSpinner from '../loadingSpinner/loadingSpinner';
import InfoIcon from '../../assets/icons/info-icon.svg';
import EmptyBigCard from '../dashboard/dashboardCards/emptyBigCard';
import { useDashboardFilterDateStore } from '../../store/dateRangeStore';
import AdsReportTable from '../../partials/adsReportTable';
import { TableColumnInterface } from '../../interfaces/tableColumn.interface';
import VatDisplayer from '../../partials/VATDisplayer';
import { useGetKeywordPerformance } from '../../apiHooks/queries/keywordPerformance/useGetKeywordPerformance';
import { useGetExportKeywordTable } from '../../apiHooks/mutations/reportsExportMutations/useGetExportKeywordTable';
import ExportButton from '../exportButton/exportButton';
import SearchIcon from '../../assets/icons/searchIconAds.png';
import { useDebounce } from '../../constants/hookConstants';
import {useCustomFieldsStore} from "../../store/customFieldsStore";

export const keywordColumn: TableColumnInterface[] = [
  {
    field: 'formSubmissions',
    headerName: 'formSubmissions',
    cellRenderer: (params) => <VatDisplayer value={params} />,
  },
  {
    field: 'income',
    headerName: 'income',
    cellRenderer: (params) => <VatDisplayer value={params} />,
  },
  {
    field: 'sales',
    headerName: 'sales',
    cellRenderer: (params) => <VatDisplayer value={params} />,
  },

  {
    field: 'cvRate',
    headerName: 'cvRate',
    cellRenderer: (params, rowObject) => (
      <span>
        {rowObject?.sales && rowObject?.formSubmissions && (
          Math.round((rowObject.sales / (rowObject.formSubmissions || 1)) * 100)
        )}
        %
      </span>
    ),
  },
];

export const referrerReportFixedColumns: TableColumnInterface[] = [
  {
    field: 'keyword',
    headerName: 'keyword',
    cellRenderer: (params: string) => (
      <Tooltip
        arrow
        placement="top"
        title={params}
      >
        <span className="2xl: w-[250px] max-w-[190px] truncate">
          {params === '' ? 'Direct' : params}
        </span>
      </Tooltip>
    ),
  },
];

interface KeywordInterfaceProps {
  adCode?: string;
}

type KeywordPerformancePagedProps = {
  adCode?: string;
  dashboardFilterDateRange: any;
  search: string;
  onSearchChange: (val: string) => void;
  showSearch: boolean;
  toggleSearch: () => void;
  closeSearch: () => void;
  selectedCustomField?: any;
};

const KeywordPerformancePaged: FC<KeywordPerformancePagedProps> = (props) => {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);

  const {
    data: referrerReport,
    isFetching,
    isError,
  } = useGetKeywordPerformance(
    'KEYWORD_PERFORMANCE',
    props.dashboardFilterDateRange[0],
    props.dashboardFilterDateRange[1],
    page || 0,
    props.adCode,
    props.search,
    props.selectedCustomField?.id,
    props.selectedCustomField?.name,
    {
      onSuccess: (resp: any) => {
        if (resp?.data?.data?.report === null && page > 0) {
          setPage(0);
        }
      },
    },
  );

  const { mutate: downloadKeyword } = useGetExportKeywordTable(
    {
      from: props.dashboardFilterDateRange[0],
      to: props.dashboardFilterDateRange[1],
      page: page || 0,
    },
  );

  const reportData = referrerReport?.data?.data?.report;

  const handlePrevPage = () => {
    setPage((prev) => prev - 1);
  };

  const handleNextPage = () => {
    setPage((prev) => prev + 1);
  };

  return (
    <div className="flex h-full flex-1 flex-col text-clip rounded-lg">
      <div
        className="flex items-center justify-between rounded-t-lg border-b bg-white px-3 pb-1 pt-2"
      >
        <div className="flex items-center gap-4 shrink-0">
          <h2 className="text-lg font-medium leading-[36px]  text-primary">
            {t('keywordPerformance')}
          </h2>
          <Tooltip title={t('keywordPerformanceTooltip')}>
            <img src={InfoIcon} alt="" className="max-w-[20px]" />
          </Tooltip>
        </div>
        <div className="flex items-center">
          <div className="mx-4 flex w-full items-center justify-end gap-2">
            {props.selectedCustomField ? <div className={"text-xs text-[#828282]"}>{t('filteredByCustomFields')}</div> : <div />}
            <ClickAwayListener onClickAway={props.closeSearch}>
              <div
                className="border-gray-300 relative flex items-center rounded border transition-all duration-500 ease-in-out"
              >
                <button
                  onClick={props.toggleSearch}
                  disabled={!reportData || reportData.length === 0 || isFetching || isError}
                  className={`flex h-8 w-8 items-center justify-center rounded transition-all duration-500 ease-in-out hover:bg-gray-200 ${
                    props.showSearch ? 'invisible w-0' : 'visible w-8'
                  } ${!reportData || reportData.length === 0 || isFetching || isError ? 'bg-[#EAEAEA] hover:cursor-not-allowed' : ''}`}
                >
                  <img src={SearchIcon} alt="Search Icon" className="h-4 w-4" />
                </button>
                <div
                  className={`outline- flex items-center overflow-hidden transition-all duration-500 ease-in-out ${
                    props.showSearch ? 'w-[150px] px-2' : 'w-0 px-0'
                  }`}
                >
                  <img
                    onClick={reportData ? props.toggleSearch : () => null}
                    src={SearchIcon}
                    alt="Search"
                    className={`absolute left-2 h-4 w-4 text-[#828282] ${!reportData || reportData.length === 0 || isFetching || isError ? 'hover:cursor-not-allowed' : ' hover:cursor-pointer'}`}
                  />
                  <input
                    type="text"
                    className={`h-8 w-full rounded border-none py-1 pl-1 pr-2 text-primary outline-none placeholder:text-gray-400 ${
                      props.showSearch ? 'visible' : 'invisible'
                    }`}
                    placeholder={`${t('Search') || 'Search'}`}
                    onChange={(e) => props.onSearchChange(e.target.value)}
                  />
                </div>
              </div>
            </ClickAwayListener>
          </div>
          <div className="flex  justify-center rounded-[6px] border px-2 py-1 shrink-0">
            <ExportButton fetchDownload={downloadKeyword} />
          </div>
        </div>
      </div>
      <div className="flex h-[499px] w-full flex-col items-center  text-center">
        {!isFetching && !isError && (reportData === null || reportData?.length === 0) && (
        <div className="flex h-full object-contain">
          <EmptyBigCard />
        </div>
        )}
        {isFetching && <LoadingSpinner center color="primary" />}
        {isError && !isFetching && (
        <p>
          {t('errorRetrievingData')}
        </p>
        )}
        {!isFetching && !isError && reportData !== null && reportData && reportData?.length > 0 && (
        <AdsReportTable
          data={referrerReport?.data?.data?.report}
          isFetching={isFetching}
          isError={isError}
          columns={keywordColumn}
          fixedColumns={referrerReportFixedColumns}
        />
        )}
      </div>
      {!isFetching && !isError && reportData !== null && reportData && reportData?.length > 0 && (
        <div className="mb-[22px] mt-3 flex items-center justify-center gap-4">
          <button
            type="button"
            onClick={handlePrevPage}
            className={`
              ${page === 0
              ? 'cursor-default opacity-30'
              : 'cursor-pointer hover:bg-primary/20 active:bg-primary/30'}
               grid h-[28px] w-[28px] place-content-center  rounded-lg bg-primary/10 
            `}
            disabled={page === 0 || isFetching}
          >
            <img src={LeftAngle} alt="" />
          </button>
          <button
            type="button"
            onClick={handleNextPage}
            className={`
              ${!referrerReport?.data?.data?.hasNextPage
              ? 'cursor-default opacity-30'
              : 'cursor-pointer hover:bg-primary/20 active:bg-primary/30'}
               grid h-[28px] w-[28px] place-content-center rounded-lg bg-primary/10
            `}
            disabled={!referrerReport?.data?.data?.hasNextPage || isFetching}
          >
            <img src={RightAngle} alt="" />
          </button>
        </div>
      )}
    </div>
  );
};

const KeywordPerformance: FC<KeywordInterfaceProps> = (props) => {
  const [search, setSearch] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const dashboardFilterDateRange = useDashboardFilterDateStore((state) => state.dashboardFilterDateRange);
  const { selectedCustomField } = useCustomFieldsStore();

  const debouncedSearch = useDebounce((val: string) => {
    setSearch(val);
  }, 500);

  const dateKey = `${dashboardFilterDateRange?.[0]?.valueOf?.() ?? 'null'}_${dashboardFilterDateRange?.[1]?.valueOf?.() ?? 'null'}`;

  return (
    <KeywordPerformancePaged
      key={dateKey}
      adCode={props.adCode}
      dashboardFilterDateRange={dashboardFilterDateRange}
      search={search}
      onSearchChange={debouncedSearch}
      showSearch={showSearch}
      toggleSearch={() => setShowSearch((prev) => !prev)}
      closeSearch={() => setShowSearch(false)}
      selectedCustomField={selectedCustomField}
    />
  );
};
export default KeywordPerformance;
